import i18n from '@/i18n'

/**
 * Which language the UI is in.
 *
 * ── Why only three ──────────────────────────────────────────────────────
 * `src/locales/` ships six files, but only three are languages. Measured
 * against en.json's 1,883 keys:
 *
 *   zh   1,716 keys, 87% genuinely translated
 *   ms     723 keys, 31% genuinely translated (the rest falls back to English,
 *          which is what vue-i18n does for a missing key — partial is honest)
 *   ar     133 keys, and ALL 133 are byte-identical to English
 *   es     133 keys, same
 *   fr     133 keys, same
 *
 * ar / es / fr are English under a different filename. Offering them would let
 * somebody pick العربية and get an English screen, which is worse than not
 * offering it. They stay on disk — deleting a translator's starting point is
 * not this file's call — they are simply not listed.
 *
 * ── Why the names are not translated ────────────────────────────────────
 * Each language is written in ITSELF (an endonym), never in the current UI
 * language. The whole point of this control is to be findable by somebody who
 * cannot read the language currently on screen — a Malay-speaking agent handed
 * a Chinese UI needs to see "Bahasa Melayu", not "马来语".
 *
 * It also sidesteps a bug class the old `languages.*` i18n keys already had:
 * zh.json translated "Chinese" as 中国人, which means "a Chinese person".
 */
const KEY = 'inbox_lang'

/** The order is deliberate: English first as the fallback everything else
 *  degrades to, then the two markets this product is sold into. */
export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'ms', name: 'Bahasa Melayu' },
  { code: 'zh', name: '中文' },
]

export const languageHelper = {
  list() {
    return LANGUAGES
  },

  /** The stored choice, or the build default. Never an unlisted code — a
   *  stale `hi` left in storage would otherwise blank the whole UI. */
  current() {
    const stored = localStorage.getItem(KEY)
    if (stored && LANGUAGES.some((l) => l.code === stored)) return stored
    return i18n.locale || 'en'
  },

  currentName() {
    const code = this.current()
    const found = LANGUAGES.find((l) => l.code === code)
    return found ? found.name : code
  },

  /**
   * Switch language.
   *
   * Sets the locale AND reloads, deliberately. vue-i18n is reactive, but a lot
   * of this app's screens build their labels once in `data()` —
   * `title: i18n.t('label.listingX', [...])`, the `fields` array every b-table
   * reads, the breadcrumb items. Those are plain values, evaluated at create
   * time, so without a reload a language change repaints the template bindings
   * and leaves every table header and page title in the old language. A
   * half-translated screen is worse than the one you started on.
   *
   * The brand picker reloads for its own reason (stale data in open panes) and
   * this follows that precedent. Changing language is a once-per-account
   * action, so the cost is paid approximately never.
   */
  set(code) {
    if (!LANGUAGES.some((l) => l.code === code)) return
    localStorage.setItem(KEY, code)
    i18n.locale = code
    window.location.reload()
  },

  /** Applied once at boot, before the first screen renders. */
  apply() {
    i18n.locale = this.current()
  },

  key: KEY,
}
